const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// KPI structure for the JSONB array
export interface KPIItem {
  name: string;
  description: string;
  code: string;
}

// Organisation KPI interface
export interface OrganisationKPIs {
  id?: number;
  organizationId: number;
  ruleId: string;
  kpis: KPIItem[];
  ruleText: string;
  ruleWeight: number;
  createdAt?: string;
  updatedAt?: string;
  isActive: boolean;
}

// Request interfaces
export interface CreateOrganisationKPIRequest {
  // ruleId is computed from database id, not provided in request
  kpis: KPIItem[];
  ruleText: string;
  ruleWeight: number;
}

export interface UpdateOrganisationKPIRequest {
  // ruleId is computed from database id, not updatable
  kpis?: KPIItem[];
  ruleText?: string;
  ruleWeight?: number;
}

class OrganisationKPIsService {
  private async getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Get organisation KPIs for user's organization
  async getOrganisationKPIs(): Promise<OrganisationKPIs[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/organisation-kpis`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch organisation KPIs: ${response.statusText}`);
      }

      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching organisation KPIs:', error);
      throw error;
    }
  }

  // Get organisation KPI by rule ID
  async getOrganisationKPIByRuleId(ruleId: string): Promise<OrganisationKPIs | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/organisation-kpis/rule/${ruleId}`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch organisation KPI: ${response.statusText}`);
      }

      const data = await response.json();
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching organisation KPI by rule ID:', error);
      throw error;
    }
  }

  // Create organisation KPI
  async createOrganisationKPI(kpiData: CreateOrganisationKPIRequest): Promise<OrganisationKPIs> {
    try {
      const response = await fetch(`${API_BASE_URL}/organisation-kpis`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(kpiData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || `Failed to create organisation KPI: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error creating organisation KPI:', error);
      throw error;
    }
  }

  // Update organisation KPI
  async updateOrganisationKPI(id: number, updates: UpdateOrganisationKPIRequest): Promise<OrganisationKPIs> {
    try {
      const response = await fetch(`${API_BASE_URL}/organisation-kpis/${id}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || `Failed to update organisation KPI: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating organisation KPI:', error);
      throw error;
    }
  }

  // Delete organisation KPI
  async deleteOrganisationKPI(id: number): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/organisation-kpis/${id}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || `Failed to delete organisation KPI: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting organisation KPI:', error);
      throw error;
    }
  }
}

export default new OrganisationKPIsService();
